import { GetEmployerDto, PayrollCycle } from '@zorro/clients';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { formatDateISO, tryFormatDateISO } from '@zorro/shared/formatters';
import { useState } from 'react';

import { useAnalytics } from '../Analytics';
import { PayrollDataTable } from './PayrollDataTable';
import { PayrollReportForm } from './PayrollReportForm/PayrollReportForm';
import { PayrollFormData } from './PayrollReportUtils';
import { useFetchPayrollReportAllViews } from './hooks/usePaginatedPayrollReports';

type Props = {
  employer: GetEmployerDto;
  isDon: boolean;
};

export const PayrollReport = ({ employer, isDon }: Props) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedPayroll, setSelectedPayroll] = useState<PayrollFormData>();
  const { sendEvent } = useAnalytics();

  const { payrollViewToDataMap, currentView, setCurrentView } =
    useFetchPayrollReportAllViews(employer.id, selectedPayroll);

  const onGenerateReportClick = async (selectedPayroll: PayrollFormData) => {
    setSelectedPayroll(selectedPayroll);
    await sendEvent('click_update_report', {
      start_date: formatDateISO(selectedPayroll.payPeriodStartDate),
      end_date: tryFormatDateISO(selectedPayroll.payPeriodEndDate),
      payroll_cycle: selectedPayroll.payrollCycle,
      indicate_changes_since: tryFormatDateISO(
        selectedPayroll.indicateChangesSince
      ),
    });
  };

  return (
    <>
      {selectedPayroll ? (
        <PayrollDataTable
          {...selectedPayroll}
          onEditReportClick={async () => {
            setIsDrawerOpen(true);
            await sendEvent('click_edit_report');
          }}
          employer={employer}
          payrollViewToDataMap={payrollViewToDataMap}
          currentView={currentView}
          setCurrentView={setCurrentView}
          isDon={isDon}
        />
      ) : (
        <PayrollReportForm
          employerPayrollCycle={employer.payrollCycle || PayrollCycle.MONTHLY}
          onGenerateReportClick={onGenerateReportClick}
        />
      )}
      <Drawer
        onClose={async () => {
          setIsDrawerOpen(false);
          await sendEvent('click_exit_update_report_drawer');
        }}
        title="Update report"
        position="right"
        opened={isDrawerOpen}
      >
        <PayrollReportForm
          isEditMode
          currentReportValues={selectedPayroll}
          employerPayrollCycle={employer.payrollCycle || PayrollCycle.MONTHLY}
          onGenerateReportClick={onGenerateReportClick}
          closeDrawer={() => setIsDrawerOpen(false)}
        />
      </Drawer>
    </>
  );
};
